import axios from 'axios';
import Holidays from 'date-holidays';

import criarCalendarioRecebimento from './calendarioRecebimento';
const calendarioRecebimento = criarCalendarioRecebimento(Holidays);

// ====== API PÚBLICA DO GLOBALUTILS ======
const globalUtils = {
  // Formatação de datas
  formatarData(data) {
    if (!data) return '';
    if (typeof data === 'string' && data.endsWith('Z')) {
      const [ano, mes, dia] = data.substring(0, 10).split('-');
      return `${dia}/${mes}/${ano}`;
    }
    const dataParaConverter = String(data).includes('T') ? data : data + 'T00:00:00';
    const d = new Date(dataParaConverter);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
  },

  formatarDataParaInput(data) {
    if (!data) return '';
    const dataParaConverter = String(data).includes('T') ? data : data + 'T00:00:00';
    const d = new Date(dataParaConverter);
    return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
  },

  formatarHora(hora) {
    if (!hora) return '';
    return String(hora).substring(0, 5);
  },

  verificarAtivo(valor) {
    if (valor === undefined || valor === null) return 'Não informado';
    return valor ? 'Ativo' : 'Inativo';
  },

  formatarValor(valor) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor ?? 0);
  },

  formatarPercentual(valor) {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor ?? 0);
  },

  converterValor(valorFormatado) {
    if (typeof valorFormatado !== 'string') return 0;
    let v = valorFormatado.replace(/[^\d,.-]/g, ''); // mantém dígitos, vírgula, ponto e sinal
    v = v.replace(/\./g, ''); // remove milhares
    v = v.replace(/,/g, '.'); // vírgula decimal -> ponto
    const num = parseFloat(v);
    return isNaN(num) ? 0 : num;
  },

  formatarCNPJ(cnpj) {
    if (!cnpj || typeof cnpj !== 'string') return '';
    const cnpjLimpo = cnpj.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) return '';
    return cnpjLimpo.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  },

  // Presentation only: do not validate or mutate the persisted document.
  formatarCEP(value) {
    const original = String(value ?? '');
    const digits = original.replace(/\D/g, '');
    return digits.length === 8 ? digits.replace(/^(\d{5})(\d{3})$/, '$1-$2') : original;
  },

  formatarDocumento(value) {
    const original = String(value ?? '').trim();
    const digits = original.replace(/\D/g, '');
    if (digits.length === 11) return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    if (digits.length === 14) return globalUtils.formatarCNPJ(digits);
    return original;
  },

  validarCPF(cpf) {
    const limpo = String(cpf ?? '').replace(/\D/g, '');

    // Exceção temporária: aceitar 000.000.000-00
    if (limpo === '00000000000') return true;

    if (limpo.length !== 11) return false;
    // Bloqueia sequências repetidas (111..., 222..., etc.)
    if (/^(\d)\1{10}$/.test(limpo)) return false;

    let soma = 0;
    for (let i = 0; i < 9; i++) soma += Number(limpo[i]) * (10 - i);
    let resto = soma % 11;
    const digito1 = resto < 2 ? 0 : 11 - resto;
    if (Number(limpo[9]) !== digito1) return false;

    soma = 0;
    for (let i = 0; i < 10; i++) soma += Number(limpo[i]) * (11 - i);
    resto = soma % 11;
    const digito2 = resto < 2 ? 0 : 11 - resto;

    return Number(limpo[10]) === digito2;
  },

  validarCNPJ(cnpj) {
    cnpj = String(cnpj).replace(/\D/g, '');
    if (cnpj.length !== 14) return false;
    if (/^(\d)\1{13}$/.test(cnpj)) return false;
    let tamanho = 12;
    let numeros = cnpj.substring(0, tamanho);
    let digitos = cnpj.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;
    for (let i = 0; i < tamanho; i++) {
      soma += parseInt(numeros.charAt(i)) * pos;
      if (--pos < 2) pos = 9;
    }
    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(0))) return false;
    tamanho = 13;
    numeros = cnpj.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;
    for (let i = 0; i < tamanho; i++) {
      soma += parseInt(numeros.charAt(i)) * pos;
      if (--pos < 2) pos = 9;
    }
    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    return resultado === parseInt(digitos.charAt(1));
  },

  async buscarEnderecoPorCEP(cep) {
    const cepLimpo = String(cep).replace(/\D/g, '');
    if (cepLimpo.length !== 8) throw new Error('CEP inválido');
    const response = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
    if (response.data.erro) throw new Error('CEP não encontrado');
    return {
      endereco: response.data.logradouro,
      complemento: response.data.complemento,
      bairro: response.data.bairro,
      municipio: response.data.localidade,
      estado: response.data.uf,
    };
  },

  gerarIDUnico() {
    return '_' + Math.random().toString(36).substr(2, 9);
  },

  valorPorExtenso(valor) {
    if (typeof valor !== 'number' || isNaN(valor) || !isFinite(valor) || valor > 999999999.99) {
      return 'Valor inválido';
    }
    const valorAbs = Math.abs(valor);
    const reais = Math.floor(valorAbs);
    const centavos = Math.round((valorAbs - reais) * 100);

    const unidades = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
    const dezenas = ['', 'dez', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
    const especiais = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
    const centenas = ['', 'cem', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];

    const converterAte999 = (num) => {
      if (num === 0) return '';
      let res = '';
      if (num >= 100) {
        const c = Math.floor(num / 100);
        if (c === 1) res += (num === 100 ? 'cem' : 'cento');
        else res += centenas[c];
        num %= 100;
        if (num > 0) res += ' e ';
      }
      if (num >= 10 && num <= 19) res += especiais[num - 10];
      else if (num >= 20) {
        const d = Math.floor(num / 10);
        res += dezenas[d];
        num %= 10;
        if (num > 0) res += ' e ';
      }
      if (num > 0 && num < 10) res += unidades[num];
      return res;
    };

    const converterReais = (num) => {
      if (num === 0) return 'zero';
      const parts = [];
      if (num >= 1000000) {
        const m = Math.floor(num / 1000000);
        parts.push(`${converterAte999(m)} ${m === 1 ? 'milhão' : 'milhões'}`);
        num %= 1000000;
      }
      if (num >= 1000) {
        const t = Math.floor(num / 1000);
        parts.push(t === 1 ? 'mil' : `${converterAte999(t)} mil`);
        num %= 1000;
      }
      if (num > 0) parts.push(converterAte999(num));
      let resultado = '';
      for (let i = 0; i < parts.length; i++) {
        resultado += parts[i];
        if (i < parts.length - 1) {
          if (parts[i].endsWith('mil') && parts[i + 1].startsWith('cento')) resultado += ' ';
          else resultado += ' e ';
        }
      }
      return resultado.trim();
    };

    let resultado = '';
    if (valor < 0) resultado += 'menos ';
    if (reais === 0 && centavos === 0) {
      resultado = 'zero reais';
    } else {
      resultado += converterReais(reais);
      resultado += reais === 1 ? ' real' : ' reais';
    }
    if (centavos > 0) {
      resultado += ' e ';
      resultado += converterAte999(centavos) + (centavos === 1 ? ' centavo' : ' centavos');
    }
    return resultado;
  },

  // === URLs & Uploads ===
  asAbsoluteUrl(relativeOrAbsolute) {
    if (!relativeOrAbsolute) return '';
    if (/^https?:\/\//i.test(relativeOrAbsolute)) return relativeOrAbsolute;
    return new URL(relativeOrAbsolute, window.location.origin).toString();
  },

  inferTenantFromHost() {
    const host = (window.location.hostname || '').toLowerCase();
    if (host.startsWith('hmg-hipica') || host.includes('hipica_hmg')) return 'hipica_hmg';
    if (host.startsWith('hmg-lagosul') || host.includes('lagosul_hmg')) return 'lagosul_hmg';
    if (host.includes('hipica')) return 'hipica';
    if (host.includes('lagosul')) return 'lagosul';
    return '';
  },

  // Agora como MÉTODO do objeto
  normalizeUploadPath(p) {
    if (!p) return '';
    if (/^https?:\/\//i.test(p)) return p;
    let s = String(p).trim();
    if (s.startsWith('uploads/')) s = '/' + s;
    if (s.startsWith('/uploads/')) {
      const rest = s.slice('/uploads/'.length);
      const first = rest.split('/')[0] || '';
      const known = new Set(['hipica', 'lagosul', 'hipica_hmg', 'lagosul_hmg']);
      if (known.has(first)) return s; // já tem tenant
      const t = this.inferTenantFromHost() || 'hipica';
      return `/uploads/${t}/${rest.replace(/^\/+/, '')}`;
    }
    const t = this.inferTenantFromHost() || 'hipica';
    return `/uploads/${t}/${s.replace(/^\/+/, '')}`;
  },

  // Também como MÉTODO do objeto
  absoluteUploadUrl(p) {
    const rel = this.normalizeUploadPath(p);
    if (!rel) return '';
    if (/^https?:\/\//i.test(rel)) return rel;
    return new URL(rel, window.location.origin).toString();
  },

  buildUploadUrl(filename, tenant) {
    if (!filename) return '';
    const t = tenant || this.inferTenantFromHost();
    const rel = `/uploads/${t}/${filename}`;
    return this.asAbsoluteUrl(rel);
  },

  // -------- NOVOS MÉTODOS --------

  /**
   * Define a UF base para feriados públicos (ex.: 'DF', 'SC', 'SP', etc.)
   * Pode chamar isso no boot da app, se quiser outro padrão.
   */
  setHolidaysUF(uf = 'DF') {
    // recria com novo estado, mantendo 'BR'
    calendarioRecebimento.setHolidaysUF(uf);
  },

  /**
   * Próximo dia útil (considerando finais de semana e feriados públicos do HD)
   * @param {Date|string} d - Date ou 'YYYY-MM-DD'
   * @returns {Date}
   */
  nextBusinessDay(d) {
    return calendarioRecebimento.nextBusinessDay(d);
  },

  /**
   * Calcula a data de recebimento conforme a forma de pagamento.
   * Regras:
   * - Espécie/Cheque/Boleto      -> mesmo dia
   * - PIX                        -> mesmo dia se dia útil; senão, próximo dia útil
   * - Transferência/TED          -> mesmo dia se dia útil; senão, próximo dia útil
   * - "A informar"              -> ''
   * - Crédito (+31) / Débito (+1) -> ajusta para dia útil
   * - Padrão: soma prazo_pagamento (dias) e ajusta para útil
   *
   * @param {string} dataPagIso  'YYYY-MM-DD'
   * @param {number|string|object} formaIdOrNameOrObj
   *        - id (number) OU nome (string) OU o próprio objeto forma { id, nome, prazo_pagamento }
   * @param {Array<object>} [formasLista] lista completa, caso passe id/number
   * @param {number} numeroParcela número da parcela para cartão parcelado
   * @returns {string} 'YYYY-MM-DD' ou ''
   */
  calcularDataRecebimento(...args) { return calendarioRecebimento.calcularDataRecebimento(...args); },

};

export default globalUtils;
